import {ChangeEvent, Dispatch, SetStateAction, useCallback} from 'react';
import {
  Alert,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  styled,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type Props = {
  selectedFiles: File[];
  setSelectedFiles: Dispatch<SetStateAction<File[]>>;
};
const FileUploads = ({selectedFiles, setSelectedFiles}: Props) => {
  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(e.target.files ?? []);
      setSelectedFiles(prevFiles => {
        const existingFileNames = prevFiles.map(file => file.name);
        const uniqueNewFiles = newFiles.filter(
          file =>
            !existingFileNames.includes(file.name) &&
            file.size <= 20 * 1024 * 1024,
        );
        return [...prevFiles, ...uniqueNewFiles];
      });
    },
    [setSelectedFiles],
  );
  const handleDeleteClick = useCallback(
    (index: number) => {
      setSelectedFiles(prevFiles => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);
        return newFiles;
      });
    },
    [setSelectedFiles],
  );

  return (
    <Grid component={List} container mt={0} rowSpacing={1} columnSpacing={1}>
      <Grid item xs={12} className='text-center'>
        <Button
          size='medium'
          component='label'
          variant='outlined'
          className='px-8'
          startIcon={<CloudUploadIcon />}>
          Click to attach file(s)
          <VisuallyHiddenInput
            type='file'
            multiple
            onChange={handleFileChange}
          />
        </Button>
        <Alert severity='info' className='mt-1 justify-center'>
          Please upload a file that is smaller than 20 MB.
        </Alert>
      </Grid>
      {selectedFiles.length > 0 &&
        selectedFiles.map((file: File, index: number) => (
          <Grid item xs={12} key={file.name} className='mb-3'>
            <ListItem
              className='px-4 py-3 radius-5'
              sx={{background: '#0d1525'}}
              secondaryAction={
                <IconButton
                  edge='end'
                  title='Remove'
                  onClick={() => handleDeleteClick(index)}>
                  <DeleteOutlineIcon />
                </IconButton>
              }>
              <Tooltip title={file.name}>
                <ListItemText
                  primary={file.name}
                  primaryTypographyProps={{noWrap: true}}
                />
              </Tooltip>
            </ListItem>
          </Grid>
        ))}
    </Grid>
  );
};
export default FileUploads;
